import { Injectable } from '@angular/core';
import { PaymentPeriod } from '@shared/definitions/loan';

/**
 * Unified Loan Calculator Service
 * 
 * This service provides centralized loan calculation functions used by both:
 * - The main portfolio loan management system (LoansService)
 * - The investment simulation system (LoanManagerService)
 * 
 * By centralizing these calculations, we ensure consistent methodology
 * across the application while reducing code duplication.
 */
@Injectable({
  providedIn: 'root'
})
export class LoanCalculatorService {

  constructor() { }

  /**
   * Calculate monthly payment for a standard amortizing loan
   * 
   * @param principal Initial loan amount
   * @param interestRate Annual interest rate (in percentage, e.g., 3.5)
   * @param termMonths Total number of months for the loan term
   * @returns Monthly payment amount
   */
  public calculateFixedRatePayment(
    principal: number,
    interestRate: number,
    termPeriods: number,
    periodsPerYear = 12
  ): number {
    // Ensure interest rate is in percentage format (e.g., 3.5 for 3.5%)
    // and convert to the per-period decimal rate. For a monthly loan
    // periodsPerYear=12 (e.g., 0.00292 for 3.5% annual); for quarterly=4,
    // yearly=1, etc. `termPeriods` is the number of payments (months for a
    // monthly loan, quarters for a quarterly loan…), NOT always months.
    const periodicRate = interestRate / 100 / periodsPerYear;
    const numberOfPayments = termPeriods;

    // Handle edge case of 0% interest
    if (periodicRate === 0) {
      return principal / numberOfPayments;
    }

    // Standard amortization formula
    return principal * (periodicRate * Math.pow(1 + periodicRate, numberOfPayments)) /
      (Math.pow(1 + periodicRate, numberOfPayments) - 1);
  }

  /**
   * Calculate monthly payment for a bullet loan (interest-only with principal at maturity)
   * 
   * @param principal Initial loan amount
   * @param interestRate Annual interest rate (in percentage)
   * @param termMonths Total number of months for the loan term
   * @param bulletAmount Amount due at maturity (defaults to full principal)
   * @returns Monthly payment amount
   */
  public calculateBulletPayment(
    principal: number, 
    interestRate: number, 
    termMonths: number, 
    bulletAmount?: number
  ): number {
    // Ensure interest rate is in percentage format (e.g., 3.5 for 3.5%)
    // and convert to monthly decimal rate (e.g., 0.00292 for 3.5% annual)
    const monthlyRate = interestRate / 100 / 12;
    
    // For bullet loans with specified bullet payment
    if (bulletAmount && bulletAmount > 0) {
      const amortizingAmount = principal - bulletAmount;
      if (amortizingAmount <= 0) {
        // Pure interest-only with balloon payment at the end
        return principal * monthlyRate;
      } else {
        // Partially amortizing with balloon payment at the end
        return this.calculateFixedRatePayment(amortizingAmount, interestRate, termMonths);
      }
    }
    
    // Default case: interest-only with full principal at the end
    return principal * monthlyRate;
  }

  /**
   * Calculate monthly payment for an interest-only loan
   * 
   * @param principal Initial loan amount
   * @param interestRate Annual interest rate (in percentage)
   * @param termMonths Total number of months for the loan term
   * @param interestOnlyPeriodMonths Number of months for the interest-only period
   * @returns Monthly payment amount for the current phase of the loan
   */
  public calculateInterestOnlyPayment(
    principal: number, 
    interestRate: number, 
    _termMonths: number,
    _interestOnlyPeriodMonths?: number
  ): number {
    // Ensure interest rate is in percentage format (e.g., 3.5 for 3.5%)
    // and convert to monthly decimal rate (e.g., 0.00292 for 3.5% annual)
    const monthlyRate = interestRate / 100 / 12;
    
    // Interest-only payment is just the interest amount
    return principal * monthlyRate;
  }

  /**
   * Calculate payment breakdown (principal vs interest) for a specific period
   * 
   * @param principal Original loan amount
   * @param interestRate Annual interest rate (percentage)
   * @param termMonths Total loan term in months
   * @param currentMonth Current month number (1-based)
   * @param remainingBalance Current remaining balance
   * @param loanType Type of loan (fixed, fixedcapital, interestonly)
   * @param additionalParams Additional parameters for special loan types
   * @returns Object containing principal and interest payment breakdown
   */
  public calculatePaymentBreakdown(
    principal: number,
    interestRate: number,
    termMonths: number,
    currentMonth: number,
    remainingBalance: number,
    loanType = 'fixed',
    additionalParams?: {
      interestOnlyPeriodMonths?: number,
      bulletAmount?: number,
      fixedPeriodMonths?: number,
      revisionPeriodMonths?: number
    },
    periodsPerYear = 12
  ): { principal: number, interest: number } {
    // `termMonths`/`currentMonth` are expressed in PAYMENT PERIODS: months for
    // a monthly loan (periodsPerYear=12, the default — unchanged for all
    // existing monthly callers, incl. the investment simulator), quarters for a
    // quarterly loan (periodsPerYear=4), years for an annual loan (=1).
    const periodicRate = interestRate / 100 / periodsPerYear;
    let periodicPayment: number;

    // Determine the per-period payment based on loan type
    switch (loanType.toLowerCase()) {
      case 'bullet':
        // For bullet loans, only pay interest until maturity
        if (currentMonth === termMonths) {
          // At maturity, pay remaining balance + interest
          const interestPayment = remainingBalance * periodicRate;
          const principalPayment = remainingBalance;
          return { principal: principalPayment, interest: interestPayment };
        } else {
          // Before maturity, only pay interest
          const interestPayment = remainingBalance * periodicRate;
          return { principal: 0, interest: interestPayment };
        }

      case 'interestonly':
        const interestOnlyPeriod = additionalParams?.interestOnlyPeriodMonths || termMonths;
        const _bulletAmount = additionalParams?.bulletAmount || principal;

        if (currentMonth <= interestOnlyPeriod) {
          // During interest-only period, pay only interest
          const interestPayment = remainingBalance * periodicRate;
          return { principal: 0, interest: interestPayment };
        } else {
          // After interest-only period, amortize normally
          periodicPayment = this.calculateFixedRatePayment(principal, interestRate, termMonths, periodsPerYear);
          break;
        }

      case 'fixed':
      case 'variable':
      default:
        periodicPayment = this.calculateFixedRatePayment(principal, interestRate, termMonths, periodsPerYear);
        break;
    }

    // For standard amortizing loans
    const interestPayment = remainingBalance * periodicRate;
    const principalPayment = Math.min(periodicPayment - interestPayment, remainingBalance);

    return {
      principal: Math.max(0, principalPayment),
      interest: interestPayment
    };
  }

  /**
   * Calculate remaining loan balance at a specific month
   * 
   * @param principal Original loan amount
   * @param interestRate Annual interest rate (percentage)
   * @param termMonths Total number of months for the loan term
   * @param currentMonth Current month number (0-based, 0 = loan start)
   * @param loanType Type of loan (fixed, fixedcapital, interestonly)
   * @param additionalParams Additional parameters for special loan types
   * @returns Remaining loan balance
   */
  public calculateRemainingBalance(
    principal: number,
    interestRate: number,
    termMonths: number,
    currentMonth: number,
    loanType = 'fixed',
    additionalParams?: {
      interestOnlyPeriodMonths?: number,
      bulletAmount?: number
    }
  ): number {
    // If we're at month 0, return the full principal
    if (currentMonth <= 0) return principal;
    
    const _monthlyRate = interestRate / 100 / 12;
    
    // Handle different loan types
    switch (loanType.toLowerCase()) {
      case 'bullet':
        // For bullet loans, the balance remains at principal until maturity
        if (currentMonth >= termMonths) {
          return 0; // Fully paid off at maturity
        } else {
          return principal; // Balance doesn't change until maturity
        }
        
      case 'interestonly':
      case 'interest-only':
        // Check if this is actually a bullet loan (has bulletAmount specified)
        if (additionalParams?.bulletAmount && additionalParams.bulletAmount > 0) {
          // This is a bullet loan disguised as interest-only
          if (currentMonth >= termMonths) {
            return 0; // Fully paid off at maturity
          } else {
            return principal; // Balance doesn't change until maturity
          }
        }
        
        // Regular interest-only loan logic
        const interestOnlyPeriodMonths = additionalParams?.interestOnlyPeriodMonths || termMonths;
        
        if (currentMonth <= interestOnlyPeriodMonths) {
          // During interest-only period, balance doesn't change
          return principal;
        } else if (currentMonth >= termMonths) {
          // After term, fully paid off
          return 0;
        } else {
          // After interest-only period, calculate remaining balance for amortizing period
          const amortizingTermMonths = termMonths - interestOnlyPeriodMonths;
          const amortizingMonth = currentMonth - interestOnlyPeriodMonths;
          return this.calculateStandardAmortizationBalance(
            principal, interestRate, amortizingTermMonths, amortizingMonth
          );
        }
        
      case 'fixedcapital':
      case 'fixed-capital':
        // For fixed capital loans, principal decreases linearly
        const monthlyPrincipalPayment = principal / termMonths;
        const principalPaid = monthlyPrincipalPayment * currentMonth;
        const remainingBalance = Math.max(0, principal - principalPaid);
        
        // If we've exceeded the term, the loan is paid off
        if (currentMonth >= termMonths) {
          return 0;
        }
        
        return remainingBalance;
      
      case 'fixed':
      default:
        return this.calculateStandardAmortizationBalance(
          principal, interestRate, termMonths, currentMonth
        );
    }
  }

  /**
   * Calculate remaining balance for a standard fixed-rate amortizing loan
   * 
   * @param principal Original loan amount
   * @param interestRate Annual interest rate (percentage)
   * @param termMonths Total loan term in months
   * @param currentMonth Current month number (0-based, 0 = loan start)
   * @returns Remaining loan balance
   * @private Internal helper method
   */
  private calculateStandardAmortizationBalance(
    principal: number,
    interestRate: number,
    termMonths: number,
    currentMonth: number
  ): number {
    // If interest rate is zero, simple linear reduction
    const monthlyRate = interestRate / 100 / 12;
    if (monthlyRate === 0) {
      const monthlyPayment = principal / termMonths;
      return Math.max(0, principal - (monthlyPayment * currentMonth));
    }
    
    // If we've exceeded the term, the loan is paid off
    if (currentMonth >= termMonths) {
      return 0;
    }
    
    // Standard formula for remaining balance
    const monthlyPayment = this.calculateFixedRatePayment(principal, interestRate, termMonths);
    const balance = principal * 
      Math.pow(1 + monthlyRate, currentMonth) - 
      (monthlyPayment / monthlyRate) * 
      (Math.pow(1 + monthlyRate, currentMonth) - 1);
    
    // Prevent floating point errors from resulting in negative balances
    return Math.max(0, balance);
  }

  /**
   * Convert between different payment frequencies
   * 
   * @param amount Amount per period
   * @param fromPeriod Source payment period
   * @param toPeriod Target payment period
   * @returns Converted amount
   */
  public convertBetweenPaymentPeriods(
    amount: number,
    fromPeriod: PaymentPeriod,
    toPeriod: PaymentPeriod
  ): number {
    const periodsPerYear = {
      [PaymentPeriod.Monthly]: 12,
      [PaymentPeriod.Quarterly]: 4,
      [PaymentPeriod.Yearly]: 1
    };
    
    // Convert to annual amount first
    const annualAmount = amount * periodsPerYear[fromPeriod];
    
    // Then convert to target period
    return annualAmount / periodsPerYear[toPeriod];
  }
  
  /**
   * Calculate total interest paid over the life of a loan
   * 
   * @param principal Original loan amount
   * @param interestRate Annual interest rate (percentage)
   * @param termMonths Total loan term in months
   * @param loanType Type of loan (fixed, bullet, interest-only)
   * @param additionalParams Additional parameters for special loan types
   * @returns Total interest paid over the life of the loan
   */
  public calculateTotalInterest(
    principal: number,
    interestRate: number,
    termMonths: number,
    loanType = 'fixed',
    additionalParams?: {
      interestOnlyPeriodMonths?: number,
      bulletAmount?: number
    }
  ): number {
    let totalInterest = 0;
    let remainingBalance = principal;
    
    for (let month = 1; month <= termMonths; month++) {
      // Get payment breakdown for this month
      const payment = this.calculatePaymentBreakdown(
        principal,
        interestRate,
        termMonths,
        month,
        remainingBalance,
        loanType,
        additionalParams
      );
      
      // Add the interest portion to our running total
      totalInterest += payment.interest;
      
      // Update the remaining balance for next iteration
      remainingBalance -= payment.principal;
    }
    
    return totalInterest;
  }

  /**
   * Calculate monthly payment for a fixed capital amortization loan
   * 
   * @param principal Initial loan amount
   * @param interestRate Annual interest rate (in percentage)
   * @param termMonths Total number of months for the loan term
   * @param currentMonth Current month for payment calculation (optional, defaults to first payment)
   * @returns Monthly payment amount (changes over time as principal is reduced)
   */
  public calculateFixedCapitalPayment(
    principal: number, 
    interestRate: number, 
    termMonths: number,
    currentMonth = 1
  ): number {
    // Ensure interest rate is in percentage format
    const monthlyRate = interestRate / 100 / 12;
    
    // For fixed capital, principal payment is fixed
    const principalPayment = principal / termMonths;
    
    // Calculate remaining principal at current month
    const monthsElapsed = currentMonth - 1;
    const principalPaid = principalPayment * monthsElapsed;
    const remainingPrincipal = Math.max(0, principal - principalPaid);
    
    // Calculate interest for the current payment
    const interestPayment = remainingPrincipal * monthlyRate;
    
    // Total payment is fixed principal + interest on remaining
    const totalPayment = principalPayment + interestPayment;
    
    return totalPayment;
  }
} 